// @flow
import { parse, stringify } from "query-string";
import type Api from "../api/index.js";
import type Options from "./options.js";
import type { Publishable } from "../api/types.js";

export type Hierarchy = Array<string>;
export type Query = { [key: string]: ?string };

// $FlowFixMe: WTF?
type H = Array<string | Publishable>;
type Q = Query;
export type To =
	| string
	| {
			query?: Q,
			search?: string,
			topliked?: boolean,
			tagged?: H,
			locale?: string,
			context?: string,
			slug?: string,
			append?: H,
			hierarchy?: H,
	  };
export type CreateOptions = {
	withContext?: boolean,
	absolute?: boolean,
};
type _Location = {
	pathname: string,
	search: string,
	protocol: string,
	host: string,
};
export type Location = string | _Location;
export type Action = "pop" | "push" | "replace";

const QUERY_OVERRIDE = {
	page: undefined,
	in: undefined,
	embedwidget: undefined,
	tags: undefined,
	zoom: undefined,
};

function parseLocation(location: Location, options?: Options) {
	const host = IS_BROWSER
		? document.location.host
		: options
		? options.absoluteHost
		: "pagestrip.com";
	const protocol = IS_BROWSER ? document.location.protocol : "https:";

	if (typeof location === "object") {
		if (!location.protocol) {
			location.protocol = protocol;
		}
		if (!location.host) {
			location.host = host;
		}
		return location;
	}
	const searchIndex = location.indexOf("?");
	const hashIndex = location.indexOf("#");
	const pathname =
		searchIndex >= 0
			? location.substring(0, searchIndex)
			: hashIndex >= 0
			? location.substring(0, hashIndex)
			: location;
	const search =
		searchIndex >= 0
			? location.substring(searchIndex, hashIndex >= 0 ? hashIndex : undefined)
			: "";
	const hash =
		hashIndex >= 0
			? location
					.split("#")
					.slice(-1)
					.reduce((acc, h) => (h ? `#${h}` : ""), "")
			: "";
	return {
		protocol,
		host,
		pathname,
		search,
		hash,
	};
}

const EMBED_RE = /[?&]embed(\W|$)/;
export function isEmbedded(location: Location) {
	const { search } = parseLocation(location);
	return search.match(EMBED_RE);
}

const Id = (pub) => (typeof pub === "string" ? pub : pub && pub.id);

/**
 * This is a utility class for handling Locations for parsing them for actionable pieces
 * of information for the player and also generating new urls which could be parsed again.
 */
class UrlResolver {
	options: Options;
	api: Api;
	hierarchy: Hierarchy;
	location: _Location;
	action: Action;
	url: string;
	locale: string;
	search: ?string = null;
	topliked: ?boolean = false;
	tagged: ?Array<string> = null;
	query: Q;
	anchor: ?string;

	constructor(
		options: Options,
		api: Api,
		location: Location,
		action: Action = "push"
	) {
		this.options = options;
		this.api = api;

		this.action = action;
		this.parse(location);
	}

	parse(location: Location) {
		const { pathname, search, hash } = (this.location = parseLocation(
			location,
			this.options
		));
		this.anchor = hash.substr(1);
		this.url = pathname + search + hash;
		const query = (this.query = parse(search));
		//query-string.parse turns multiple occurences of the same search parameter into an
		//array but we only want a single string value. So let's just use the first one in the array.
		if (query.in && Array.isArray(query.in)) {
			query.in = query.in[0];
		}

		const { contextInUrl } = this.options;
		const hierarchy = (this.hierarchy = []);

		const fragments = pathname
			.replace(this.options.basePath, "")
			.split("/")
			.filter(Boolean);
		this.locale =
			(this.options.localeInQuery ? query.l : fragments.shift()) ||
			this.options.locale;
		if (contextInUrl) {
			fragments.shift();
		}

		if (fragments[0] === "search") {
			this.search = query.q;
		} else if (fragments[0] === "topliked") {
			this.topliked = true;
			query.topliked = null;
		} else if (fragments[0] === "tagged") {
			this.tagged = (query.t || "").split(",");
		} else {
			const id = fragments[0];
			if (query.in) {
				hierarchy.push(...query.in.split("_"));
			}
			if (id && String(id) !== String(parseInt(id, 10))) {
				hierarchy.push(id);
			}
		}
	}

	_getSlug(id: string) {
		const result = this.api.resolveSync(id, true);
		if (!result) {
			throw new Error(`unresolved content "${id}"`);
		}
		return result.slug;
	}
	_contextSlug(id: string) {
		const result = this.api.resolveSync(id, true);
		if (!result) {
			throw new Error(`unresolved content "${id}"`);
		}
		if (result.type === "collection" || result.type === "story") {
			return result.publisher.slug;
		}
		return result.slug;
	}

	redirect(to: To) {
		this.parse(this.create(to, { absolute: false }));
	}

	create(to: To, options: CreateOptions = {}): string {
		if (typeof to === "string") {
			return to;
		}

		const jackInTheBox =
			this.options.jackInTheBox &&
			(this.options.history === "hash" || this.options.history === "query" || this.options.historyBaseName);

		options.absolute =
			options.absolute ||
			(!to.search && options.absolute === undefined && jackInTheBox) ||
			to.absolute;

		let hierarchy = (to.hierarchy || this.hierarchy).map(Id);
		if (to.append) {
			const appends = to.append
				.map(Id)
				.filter((item) => item && !hierarchy.includes(item));
			// XXX: flow somehow barfs because of this concat?
			hierarchy = (hierarchy.concat(appends): any);
		}
		hierarchy = hierarchy.filter(Boolean);

		//remove loops from hierarchy that might be generated due to internal links
		const nHierarchy = [];
		for (let i = 0; i < hierarchy.length; i++) {
			const item = hierarchy[i];
			const next = hierarchy.indexOf(item, i + 1);
			if (~next) {
				i = next;
			}
			nHierarchy.push(item);
		}
		hierarchy = nHierarchy;

		let { contextInUrl } = this.options;
		let { absolute } = options;

		if (
			"string" === typeof absolute &&
			this.options.historyBaseName &&
			/^\/[^\/]/.test(absolute) &&
			!absolute.startsWith(this.options.historyBaseName)
		) {
			absolute = this.options.historyBaseName + absolute.substring(1);
		}

		if (to.context && to.context !== this.options.context) {
			// changing context implies absolute urls, because we could be using a
			// custom domain
			absolute = absolute || true;
			contextInUrl =
				!!~(
					absolute && absolute.length > 1 ? absolute : this.options.absoluteHost
				).indexOf("pagestrip.com") && this._contextSlug(to.context);
		} else if (
			options.withContext ||
			(absolute &&
				!!~(
					absolute && absolute.length ? absolute : this.options.absoluteHost
				).indexOf("pagestrip.com") &&
				!contextInUrl &&
				!jackInTheBox)
		) {
			// absolute implies we may need to put the context into the url!
			const context =
				this.options.context ||
				(hierarchy.length ? hierarchy[hierarchy.length - 1] : "");
			contextInUrl = this._contextSlug(context);
		}

		const basePath = jackInTheBox && this.options.historyBaseName
			? `${this.options.historyBaseName}`
			: "/";

		let url = absolute
			? absolute.length
				? absolute
				: `${this.location.protocol}//${this.options.absoluteHost}${basePath}`
			: basePath;
		url += `${this.options.basePath || ""}`;

		if (!this.options.localeInQuery) {
			url += (to.locale || this.options.locale) + "/";
		}
		if (contextInUrl && options.withContext !== false) {
			url += `${contextInUrl}/`;
		}

		// pathname & query params
		const needsOverride =
			to.hierarchy ||
			to.context ||
			to.append ||
			to.search ||
			to.query ||
			to.tagged;
		const override = needsOverride ? Object.assign({}, QUERY_OVERRIDE) : null;
		if (override && to.append) {
			delete override.tags;
		}
		const query = Object.assign({}, this.query, override, to.query);
		const queryWhiteList = [
			"in",
			"page",
			"locale",
			"l",
			"zoom",
			"player_branch",
			"player_debug",
			"embedwidget",
			"t", // for zumtobel calculatore
			"a", // for zumtobel calculatore
			"auth_key",
			"tags",
		];
		Object.keys(query).forEach((key) => {
			if (
				!queryWhiteList.includes(key) &&
				!(override && override.hasOwnProperty(key)) &&
				!(to.query && to.query.hasOwnProperty(key))
			)
				delete query[key];
		});
		if (this.options.localeInQuery) {
			query.l = to.locale || this.options.locale;
		}

		if (
			to.search ||
			(this.search &&
				!to.append &&
				!to.hierarchy &&
				(!to.query || !to.query.hasOwnProperty("q")))
		) {
			url += "search/";
			query.q = to.search || this.search;
		} else if (to.topliked) {
			url += "topliked/";
			delete query.topliked;
		} else if (to.tagged) {
			url += "tagged/";
			query.t = (to.tagged || this.tagged).join(",");
		} else {
			if (hierarchy.length) {
				const last = hierarchy[hierarchy.length - 1];
				url += `${last}/${to.slug || this._getSlug(last)}/`;
			}
			if (
				hierarchy.length > 1 &&
				(!to.query || !to.query.hasOwnProperty("in"))
			) {
				query.in = hierarchy.slice(0, -1).join("_");
			}
		}

		if (to.zoom) {
			query.zoom = to.zoom;
		}

		if (query.zoom === "1" || query.zoom === 1) {
			delete query.zoom;
		}

		// query string
		const search = stringify(query);
		if (search) {
			url += `${
				absolute && this.options.history === "query" ? "&" : "?"
			}${search}`;
		}

		// anchor string
		if (to.anchor) {
			url += `#${to.anchor}`;
		}
		return url;
	}
}

export default UrlResolver;



// WEBPACK FOOTER //
// ./lib/player/urlresolver.js